import { useMemo } from 'react';
import { MdOutlineChevronRight } from 'react-icons/md';
import { ChangeLogItemProps } from '@lp/features/RoutePlanner/components/LogisticsChangeLog/consts';
import {
  isAddRemoveAccommodationLegLogs,
  isAddRemoveRouteLegLogs,
  isHubLogs,
  isModifiedAccommodationLegLog,
  isModifiedRouteLegLogs,
  getCurrentEventValues,
  getCurrentLocationValues,
  isGroundTripAssignedLogs,
  isGroundTripReassignedLogs,
} from '@lp/features/RoutePlanner/components/LogisticsChangeLog/utils';
import { LogisticsChangeLogType } from '@/api/apis/route';
import { ChangeLogLocationIcon } from './ChangeLogLocationIcon';
import { ChangeLogAccommodationDates } from './ChangeLogAccommodationDates';
import { ChangeLogLocationName } from './ChangeLogLocationName';
import { ChangeLogResourceItem } from './ChangeLogResourceItem';

export const ChangeLogLocations = ({ log }: ChangeLogItemProps) => {
  const isAddRemoveAccommodationLog = isAddRemoveAccommodationLegLogs(log);
  const isModifiedAccommodationLog = isModifiedAccommodationLegLog(log);
  const isAddRemoveRouteLegLog = isAddRemoveRouteLegLogs(log);
  const isModifiedRouteLegLog = isModifiedRouteLegLogs(log);
  const isGroundTripLogs = isGroundTripAssignedLogs(log);
  const isTripReassignedLogs = isGroundTripReassignedLogs(log);
  const isHubLog = isHubLogs(log);
  const isResourceRemovedLog = log.$type === LogisticsChangeLogType.LogisticsResourceRemovedLog;
  const isResourceSwappedLog = log.$type === LogisticsChangeLogType.LogisticsResourceSwappedLog;
  const isResourceLog = isResourceRemovedLog || isResourceSwappedLog;

  const segmentType = useMemo(() => {
    if (isAddRemoveRouteLegLog) return log.segmentType;
    if (isModifiedRouteLegLog) return log.leg.segmentType;
    return undefined;
  }, [log, isAddRemoveRouteLegLog, isModifiedRouteLegLog]);

  const accommodationLogData = useMemo(() => {
    if (isAddRemoveAccommodationLog) {
      return {
        checkInAt: log.checkInAt,
        checkOutAt: log.checkOutAt,
      };
    }

    if (isModifiedAccommodationLog) {
      return {
        checkInAt: getCurrentEventValues(log.leg.checkInAt),
        checkOutAt: getCurrentEventValues(log.leg.checkOutAt),
      };
    }

    return null;
  }, [log, isAddRemoveAccommodationLog, isModifiedAccommodationLog]);

  const legLocations = useMemo(() => {
    if (isAddRemoveRouteLegLog || isGroundTripLogs) {
      return {
        departureLocation: log.departureLocation,
        arrivalLocation: log.arrivalLocation,
      };
    }

    if (isTripReassignedLogs) {
      return {
        departureLocation: log.leg.departureLocation,
        arrivalLocation: log.leg.arrivalLocation,
      };
    }

    if (isModifiedRouteLegLog) {
      return {
        departureLocation: getCurrentLocationValues(log, 'departureLocation'),
        arrivalLocation: getCurrentLocationValues(log, 'arrivalLocation'),
      };
    }

    return null;
  }, [log, isAddRemoveRouteLegLog, isModifiedRouteLegLog, isGroundTripLogs, isTripReassignedLogs]);

  const logResources = useMemo(() => {
    if (!isResourceSwappedLog && !isResourceRemovedLog) return null;

    return {
      oldResource: isResourceRemovedLog ? log.resource : log.previousResource,
      newResource: isResourceRemovedLog ? null : log.newResource,
    };
  }, [log, isResourceSwappedLog, isResourceRemovedLog]);

  const isAccommodationLog = isAddRemoveAccommodationLog || isModifiedAccommodationLog;
  const isRouteLegLog = isAddRemoveRouteLegLog || isModifiedRouteLegLog || isGroundTripLogs || isTripReassignedLogs;

  return (
    <div className="flex gap-1.5">
      {!isResourceLog && (
        <div className="h-5 flex items-center">
          <ChangeLogLocationIcon logType={log.$type} segmentType={segmentType} />
        </div>
      )}
      {isAccommodationLog && accommodationLogData && (
        <ChangeLogAccommodationDates
          checkInAt={accommodationLogData.checkInAt}
          checkOutAt={accommodationLogData.checkOutAt}
        />
      )}
      {isRouteLegLog && legLocations && (
        <span className="leading-[normal]">
          <ChangeLogLocationName location={legLocations.departureLocation} />
          <MdOutlineChevronRight size={16} className="text-base-10 inline mx-1" />
          <ChangeLogLocationName location={legLocations.arrivalLocation} />
        </span>
      )}
      {isHubLog && <ChangeLogLocationName location={log.location} />}
      {isResourceLog && logResources && (
        <div className="flex gap-x-1 flex-wrap items-center">
          <ChangeLogResourceItem resource={logResources.oldResource} />
          {logResources.newResource && (
            <>
              <MdOutlineChevronRight size={16} className="text-base-10 inline" />
              <ChangeLogResourceItem resource={logResources.newResource} />
            </>
          )}
        </div>
      )}
    </div>
  );
};
